import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Loader2,
  LogOut,
  Package,
  Percent,
  ShoppingBag,
  Sparkles,
  Tag,
  ListOrdered,
  LayoutPanelTop,
  Image as ImageIcon,
  Truck,
  Grid,
  Banknote,
} from 'lucide-react';
import { clearAuthTokens, getAuthToken } from '@/services/memberApi';
import { shopAdminFetch, shopAdminLogin, shopAdminMe } from '@/services/shopAdminApi';
import { BannersTab } from '@/components/shop-admin/BannersTab';
import { SplashScreensTab } from '@/components/shop-admin/SplashScreensTab';
import { DiscountsTab } from '@/components/shop-admin/DiscountsTab';
import { ShippingRulesTab } from '@/components/shop-admin/ShippingRulesTab';

type Tab = 'overview' | 'products' | 'categories' | 'coupons' | 'orders' | 'promotions' | 'banners' | 'splashScreens' | 'discounts' | 'shippingRules';

interface WooProduct {
  id: number;
  name: string;
  sku?: string;
  regular_price?: string;
  sale_price?: string;
  status?: string;
  stock_quantity?: number | null;
  stock_status?: string;
}

interface DashboardOverview {
  revenue30d: number;
  orders30d: number;
  skuCount: number;
  lowStockProducts: WooProduct[];
}

interface WooCategory {
  id: number;
  name: string;
  slug: string;
  count?: number;
}

interface WooCoupon {
  id: number;
  code: string;
  amount?: string;
  discount_type?: string;
  status?: string;
}

interface WooOrder {
  id: number;
  status?: string;
  total?: string;
  currency?: string;
  date_created?: string;
}

interface ShopPromotion {
  id: string;
  title: string;
  promo_type: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  coupon_code: string | null;
}

export default function ShopBackofficePage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<{ email: string; first_name: string; surname: string } | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [products, setProducts] = useState<WooProduct[]>([]);
  const [categories, setCategories] = useState<WooCategory[]>([]);
  const [coupons, setCoupons] = useState<WooCoupon[]>([]);
  const [orders, setOrders] = useState<WooOrder[]>([]);
  const [promotions, setPromotions] = useState<ShopPromotion[]>([]);

  const [promoTitle, setPromoTitle] = useState('');
  const [promoType, setPromoType] = useState('banner');

  const verifySession = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setAuthChecked(true);
      return;
    }
    try {
      const me = await shopAdminMe();
      setProfile({ email: me.email, first_name: me.first_name, surname: me.surname });
    } catch {
      setProfile(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    verifySession();
  }, [verifySession]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await shopAdminLogin(loginEmail.trim(), loginPassword);
      setLoginPassword('');
      await verifySession();
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  const loadTabData = useCallback(async () => {
    if (!profile) return;
    setError('');
    setLoading(true);
    try {
      if (tab === 'overview') {
        const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        const [productsRaw, ordersRaw] = await Promise.all([
          shopAdminFetch<WooProduct[] | { data?: WooProduct[] }>('/wc/products?per_page=100'),
          shopAdminFetch<WooOrder[] | { data?: WooOrder[] }>('/wc/orders?per_page=50&orderby=date&order=desc'),
        ]);
        const allProducts = Array.isArray(productsRaw) ? productsRaw : productsRaw?.data || [];
        const allOrders = Array.isArray(ordersRaw) ? ordersRaw : ordersRaw?.data || [];
        const orders30d = allOrders.filter(o => o.date_created && o.date_created >= since);
        const revenue30d = orders30d
          .filter(o => o.status === 'completed' || o.status === 'processing')
          .reduce((sum, o) => sum + (parseFloat(o.total || '0') || 0), 0);
        const lowStockProducts = allProducts
          .filter(p => p.stock_status === 'outofstock' || (typeof p.stock_quantity === 'number' && p.stock_quantity <= 10))
          .sort((a, b) => (a.stock_quantity ?? 0) - (b.stock_quantity ?? 0));
        setOverview({
          revenue30d,
          orders30d: orders30d.length,
          skuCount: allProducts.length,
          lowStockProducts,
        });
      } else if (tab === 'products') {
        const data = await shopAdminFetch<WooProduct[]>('/wc/products?per_page=50');
        setProducts(Array.isArray(data) ? data : []);
      } else if (tab === 'categories') {
        const data = await shopAdminFetch<WooCategory[] | { data?: WooCategory[] }>('/wc/products/categories?per_page=100');
        const list = Array.isArray(data) ? data : data?.data;
        setCategories(Array.isArray(list) ? list : []);
      } else if (tab === 'coupons') {
        const data = await shopAdminFetch<WooCoupon[] | { data?: WooCoupon[] }>('/wc/coupons?per_page=50');
        const list = Array.isArray(data) ? data : data?.data;
        setCoupons(Array.isArray(list) ? list : []);
      } else if (tab === 'orders') {
        const data = await shopAdminFetch<WooOrder[] | { data?: WooOrder[] }>('/wc/orders?per_page=30');
        const list = Array.isArray(data) ? data : data?.data;
        setOrders(Array.isArray(list) ? list : []);
      } else if (tab === 'promotions') {
        const data = await shopAdminFetch<ShopPromotion[]>('/promotions');
        setPromotions(Array.isArray(data) ? data : []);
      }
      // banners, splashScreens, discounts, shippingRules manage their own data
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [profile, tab]);

  useEffect(() => {
    if (!profile) return;
    void loadTabData();
  }, [profile, loadTabData]);

  async function createPromotion(e: React.FormEvent) {
    e.preventDefault();
    if (!promoTitle.trim()) return;
    setLoading(true);
    setError('');
    try {
      await shopAdminFetch('/promotions', {
        method: 'POST',
        body: JSON.stringify({
          title: promoTitle.trim(),
          promo_type: promoType,
          is_active: true,
        }),
      });
      setPromoTitle('');
      await loadTabData();
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'Could not create promotion');
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    clearAuthTokens();
    setProfile(null);
    setProducts([]);
    setCategories([]);
    setCoupons([]);
    setOrders([]);
    setPromotions([]);
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#F7F5EE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BDD630]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#F7F5EE] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#D9B38C]/25 shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#567D38]/10 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-[#567D38]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#1A2E12]">Shop back office</h1>
              <p className="text-sm text-[#6B7B6E]">Catalogue, coupons, and promotions</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-[#5E6B52] mb-1">Email</label>
              <input
                type="email"
                autoComplete="username"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                className="w-full border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#BDD630]"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[#5E6B52] mb-1">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                className="w-full border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#BDD630]"
                required
              />
            </div>
            {error && (
              <div className="flex gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#567D38] text-white font-semibold py-2.5 rounded-xl hover:bg-[#1d4a30] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign in
            </button>
          </form>
          <p className="text-xs text-[#6B7B6E] mt-6 text-center">
            Access is limited to designated shop administrators. Use the same account as the member portal.
          </p>
        </div>
      </div>
    );
  }

  const NAV: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: Grid },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'categories', label: 'Categories', icon: Tag },
    { id: 'coupons', label: 'Woo Coupons', icon: Percent },
    { id: 'orders', label: 'Orders', icon: ListOrdered },
    { id: 'promotions', label: 'Campaigns & specials', icon: Sparkles },
    { id: 'banners', label: 'Banners', icon: LayoutPanelTop },
    { id: 'splashScreens', label: 'Splash screens', icon: ImageIcon },
    { id: 'discounts', label: 'Local discounts', icon: Percent },
    { id: 'shippingRules', label: 'Shipping rules', icon: Truck },
  ];

  return (
    <div className="min-h-screen bg-[#F7F5EE] flex">
      <aside className="w-64 bg-[#567D38] text-white flex flex-col shrink-0">
        <div className="px-6 py-5 border-b border-white/10">
          <p className="font-display text-xl font-bold tracking-wide text-[#BDD630]">Shop office</p>
          <p className="text-xs text-white/60 mt-0.5 truncate">{profile.email}</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(item => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  active ? 'bg-[#BDD630] text-[#1A2E12]' : 'text-white/80 hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/10"
          >
            <ShoppingBag className="w-4 h-4" />
            Agent admin
          </button>
          <button
            type="button"
            onClick={() => navigate('/member/dashboard')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/10"
          >
            Member portal
          </button>
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="px-8 py-8 max-w-6xl">
          <h1 className="text-2xl font-semibold text-[#1A2E12] mb-2">
            {NAV.find(n => n.id === tab)?.label}
          </h1>
          <p className="text-sm text-[#6B7B6E] mb-6">
            Data is loaded from WooCommerce (server-side) and stored campaigns. Configure Woo keys on the API gateway.
          </p>

          {error && (
            <div className="mb-4 flex gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-[#5E6B52] mb-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}

          {tab === 'overview' && (
            <div className="space-y-6">
              {overview ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#BDD630]/20 text-[#567D38]">
                      <Banknote className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1A2E12]">R{overview.revenue30d.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}</p>
                      <p className="text-sm text-[#5E6B52]">Revenue (30d)</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#BDD630]/20 text-[#567D38]">
                      <ListOrdered className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1A2E12]">{overview.orders30d}</p>
                      <p className="text-sm text-[#5E6B52]">Orders (30d)</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 text-blue-600">
                      <Package className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1A2E12]">{overview.skuCount}</p>
                      <p className="text-sm text-[#5E6B52]">SKUs</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-100 text-amber-600">
                      <AlertCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-[#1A2E12]">{overview.lowStockProducts.length}</p>
                      <p className="text-sm text-[#5E6B52]">Low / out of stock</p>
                      {overview.lowStockProducts.length > 0 && (
                        <p className="text-xs font-medium text-[#567D38] mt-1">Needs restock</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : !loading && (
                <p className="text-sm text-[#6B7B6E]">No overview data yet.</p>
              )}

              {overview && overview.lowStockProducts.length > 0 && (
                <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                  <div className="px-5 pt-5 pb-3 text-xs font-semibold uppercase tracking-wider text-[#5E6B52]">Restock priority</div>
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F5EE] text-[#5E6B52]">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Product</th>
                        <th className="text-left px-4 py-3 font-medium">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.lowStockProducts.map(p => {
                        const outOfStock = p.stock_status === 'outofstock' || p.stock_quantity === 0;
                        return (
                          <tr key={p.id} className="border-t border-[#D9B38C]/15">
                            <td className="px-4 py-2.5 font-medium text-[#1A2E12]">{p.name}</td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                outOfStock ? 'bg-red-50 text-red-600' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {outOfStock ? 'Out of stock' : `Low stock · ${p.stock_quantity ?? 0}`}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'products' && (
            <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F5EE] text-[#5E6B52]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">ID</th>
                    <th className="text-left px-4 py-3 font-medium">Name</th>
                    <th className="text-left px-4 py-3 font-medium">SKU</th>
                    <th className="text-left px-4 py-3 font-medium">Price</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id} className="border-t border-[#D9B38C]/15">
                      <td className="px-4 py-2.5 text-[#6B7B6E]">{p.id}</td>
                      <td className="px-4 py-2.5 text-[#1A2E12]">{p.name}</td>
                      <td className="px-4 py-2.5">{p.sku || '—'}</td>
                      <td className="px-4 py-2.5">
                        {p.sale_price ? (
                          <span>
                            <span className="text-rose-600">{p.sale_price}</span>{' '}
                            <span className="text-[#6B7B6E] line-through text-xs">{p.regular_price}</span>
                          </span>
                        ) : (
                          p.regular_price || '—'
                        )}
                      </td>
                      <td className="px-4 py-2.5 capitalize">{p.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length === 0 && !loading && (
                <p className="px-4 py-8 text-center text-[#6B7B6E]">No products returned. Check WooCommerce credentials or filters.</p>
              )}
            </div>
          )}

          {tab === 'categories' && (
            <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F5EE] text-[#5E6B52]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">ID</th>
                    <th className="text-left px-4 py-3 font-medium">Name</th>
                    <th className="text-left px-4 py-3 font-medium">Slug</th>
                    <th className="text-left px-4 py-3 font-medium">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(c => (
                    <tr key={c.id} className="border-t border-[#D9B38C]/15">
                      <td className="px-4 py-2.5 text-[#6B7B6E]">{c.id}</td>
                      <td className="px-4 py-2.5 text-[#1A2E12]">{c.name}</td>
                      <td className="px-4 py-2.5">{c.slug}</td>
                      <td className="px-4 py-2.5">{c.count ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {categories.length === 0 && !loading && (
                <p className="px-4 py-8 text-center text-[#6B7B6E]">No categories.</p>
              )}
            </div>
          )}

          {tab === 'coupons' && (
            <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F5EE] text-[#5E6B52]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">ID</th>
                    <th className="text-left px-4 py-3 font-medium">Code</th>
                    <th className="text-left px-4 py-3 font-medium">Type</th>
                    <th className="text-left px-4 py-3 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map(c => (
                    <tr key={c.id} className="border-t border-[#D9B38C]/15">
                      <td className="px-4 py-2.5 text-[#6B7B6E]">{c.id}</td>
                      <td className="px-4 py-2.5 font-mono text-[#1A2E12]">{c.code}</td>
                      <td className="px-4 py-2.5">{c.discount_type || '—'}</td>
                      <td className="px-4 py-2.5">{c.amount ?? '—'}</td>
                      <td className="px-4 py-2.5 capitalize">{c.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {coupons.length === 0 && !loading && (
                <p className="px-4 py-8 text-center text-[#6B7B6E]">No coupons.</p>
              )}
            </div>
          )}

          {tab === 'orders' && (
            <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F5EE] text-[#5E6B52]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">ID</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium">Total</th>
                    <th className="text-left px-4 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id} className="border-t border-[#D9B38C]/15">
                      <td className="px-4 py-2.5 text-[#6B7B6E]">#{o.id}</td>
                      <td className="px-4 py-2.5 capitalize">{o.status || '—'}</td>
                      <td className="px-4 py-2.5">
                        {o.total ?? '—'} {o.currency || ''}
                      </td>
                      <td className="px-4 py-2.5 text-[#6B7B6E]">
                        {o.date_created ? new Date(o.date_created).toLocaleString('en-ZA') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && !loading && (
                <p className="px-4 py-8 text-center text-[#6B7B6E]">No orders.</p>
              )}
            </div>
          )}

          {tab === 'banners' && <BannersTab />}

          {tab === 'splashScreens' && <SplashScreensTab />}

          {tab === 'discounts' && <DiscountsTab />}

          {tab === 'shippingRules' && <ShippingRulesTab />}

          {tab === 'promotions' && (
            <div className="space-y-6">
              <form onSubmit={createPromotion} className="bg-white rounded-2xl border border-[#D9B38C]/20 p-5 flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm text-[#5E6B52] mb-1">Campaign title</label>
                  <input
                    value={promoTitle}
                    onChange={e => setPromoTitle(e.target.value)}
                    className="w-full border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm"
                    placeholder="e.g. Spring ritual"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#5E6B52] mb-1">Type</label>
                  <select
                    value={promoType}
                    onChange={e => setPromoType(e.target.value)}
                    className="border border-[#D9B38C]/40 rounded-xl px-3 py-2 text-sm bg-white"
                  >
                    <option value="banner">Site banner</option>
                    <option value="category">Category focus</option>
                    <option value="product">Product spotlight</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={loading || !promoTitle.trim()}
                  className="px-5 py-2.5 bg-[#BDD630] text-[#1A2E12] font-semibold rounded-xl hover:bg-[#97c23c] disabled:opacity-50"
                >
                  Add campaign
                </button>
              </form>

              <div className="bg-white rounded-2xl border border-[#D9B38C]/20 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F5EE] text-[#5E6B52]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Title</th>
                      <th className="text-left px-4 py-3 font-medium">Type</th>
                      <th className="text-left px-4 py-3 font-medium">Active</th>
                      <th className="text-left px-4 py-3 font-medium">Coupon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotions.map(p => (
                      <tr key={p.id} className="border-t border-[#D9B38C]/15">
                        <td className="px-4 py-2.5 text-[#1A2E12]">{p.title}</td>
                        <td className="px-4 py-2.5">{p.promo_type}</td>
                        <td className="px-4 py-2.5">{p.is_active ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-2.5 font-mono text-xs">{p.coupon_code || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {promotions.length === 0 && !loading && (
                  <p className="px-4 py-8 text-center text-[#6B7B6E]">No local campaigns yet.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
